/**
 * Tiny zero-dependency confetti. Spawns a full-screen <canvas>, throws a fan of
 * gravity-driven paper bits from an origin, fades them, then removes itself.
 * No npm dep (html2canvas/canvas-confetti not needed). Respects reduced-motion.
 *
 * Colours pull from the warm palette so it reads on the "Cozy" olive paper.
 */
const COLORS = ['#7fae5f', '#e0b450', '#d98b6a', '#6f93a8', '#caa24a', '#9bc36f'];

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

interface Bit {
  x: number; y: number; vx: number; vy: number;
  rot: number; vr: number; size: number; color: string;
}

/** Fire a single burst of `count` bits from viewport point (x, y). */
export function confettiBurst(x: number, y: number, count = 22): void {
  if (typeof document === 'undefined' || prefersReducedMotion()) return;

  const canvas = document.createElement('canvas');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  Object.assign(canvas.style, {
    position: 'fixed', inset: '0', width: '100%', height: '100%',
    pointerEvents: 'none', zIndex: '130',
  });
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) { canvas.remove(); return; }
  ctx.scale(dpr, dpr);

  const bits: Bit[] = Array.from({ length: count }, (_, i) => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.7; // upward fan
    const speed = 3 + Math.random() * 6;
    return {
      x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.32,
      size: 5 + Math.random() * 6,
      color: COLORS[(i + Math.floor(Math.random() * COLORS.length)) % COLORS.length],
    };
  });

  let frame = 0;
  const tick = () => {
    frame += 1;
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    let alive = false;
    const fade = Math.max(0, 1 - frame / 95);
    for (const b of bits) {
      b.vy += 0.16; // gravity
      b.vx *= 0.99;
      b.x += b.vx;
      b.y += b.vy;
      b.rot += b.vr;
      if (fade > 0 && b.y < window.innerHeight + 24) alive = true;
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(b.x, b.y);
      ctx.rotate(b.rot);
      ctx.fillStyle = b.color;
      ctx.fillRect(-b.size / 2, -b.size / 2, b.size, b.size * 0.62);
      ctx.restore();
    }
    if (alive && frame < 130) requestAnimationFrame(tick);
    else canvas.remove();
  };
  requestAnimationFrame(tick);
}

/** A celebratory volley across the top of the screen — used when the wall opens
 *  or is replayed. Staggered so it feels like a cheer, not a single pop. */
export function confettiVolley(): void {
  if (prefersReducedMotion()) return;
  const w = window.innerWidth;
  const spots = [0.2, 0.5, 0.8];
  spots.forEach((fx, i) => {
    window.setTimeout(() => confettiBurst(w * fx, window.innerHeight * 0.32, 26), i * 220);
  });
}
